import "core-js/modules/es.object.keys.js";
import PropTypes from "prop-types";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import { formatAuto, formatBoolean, formatDate, formatNumber, formatText, LEFT_ALIGNMENT, RIGHT_ALIGNMENT, SortDirection, RenderWithEuiTheme } from '../../services';
import { isFunction } from '../../services/predicate';
import { get } from '../../services/objects';
import { EuiFlexGroup, EuiFlexItem } from '../flex';
import { EuiCheckbox } from '../form';
import { EuiComponentDefaultsContext } from '../provider/component_defaults';
import { euiTablePaginationDefaults } from '../table/table_pagination';
import { EuiTable, EuiTableBody, EuiTableFooter, EuiTableFooterCell, EuiTableHeader, EuiTableHeaderCell, EuiTableHeaderCellCheckbox, EuiTableHeaderMobile, EuiTableRow, EuiTableRowCell, EuiTableRowCellCheckbox, EuiTableSortMobile } from '../table';
import { CollapsedItemActions } from './collapsed_item_actions';
import { ExpandedItemActions } from './expanded_item_actions';
import { PaginationBar } from './pagination_bar';
import { EuiIcon } from '../icon';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiI18n } from '../i18n';
import { EuiDelayRender } from '../delay_render';
import { htmlIdGenerator } from '../../services/accessibility';
import { euiBasicTableBodyLoading, safariLoadingWorkaround, euiBasicTableActionsWrapper } from './basic_table.styles';
var dataTypesProfiles = {
  auto: {
    align: LEFT_ALIGNMENT,
    render: function render(value) {
      return formatAuto(value);
    }
  },
  string: {
    align: LEFT_ALIGNMENT,
    render: function render(value) {
      return formatText(value);
    }
  },
  number: {
    align: RIGHT_ALIGNMENT,
    render: function render(value) {
      return formatNumber(value);
    }
  },
  boolean: {
    align: LEFT_ALIGNMENT,
    render: function render(value) {
      return formatBoolean(value);
    }
  },
  date: {
    align: LEFT_ALIGNMENT,
    render: function render(value) {
      return formatDate(value);
    }
  }
};
var DATA_TYPES = Object.keys(dataTypesProfiles);
export function getItemId(item, itemId) {
  if (itemId) {
    if (isFunction(itemId)) {
      return itemId(item);
    }
    // @ts-ignore never mind about the index signature
    return item[itemId];
  }
}
function getRowProps(item, rowProps) {
  if (rowProps) {
    if (isFunction(rowProps)) {
      return rowProps(item);
    }
    return rowProps;
  }
  return {};
}
function getCellProps(item, column, cellProps) {
  if (cellProps) {
    if (isFunction(cellProps)) {
      return cellProps(item, column);
    }
    return cellProps;
  }
  return {};
}
function getColumnFooter(column, _ref) {
  var items = _ref.items,
    pagination = _ref.pagination;
  var _ref2 = column,
    footer = _ref2.footer;
  if (footer) {
    if (isFunction(footer)) {
      return footer({
        items: items,
        pagination: pagination
      });
    }
    return footer;
  }
  return undefined;
}
function hasPagination(x) {
  return x.hasOwnProperty('pagination') && !!x.pagination;
}
export class EuiBasicTable extends Component {
  static contextType = EuiComponentDefaultsContext;
  static defaultProps = {
    responsive: true,
    tableLayout: 'fixed',
    noItemsMessage: <EuiI18n token="euiBasicTable.noItemsMessage" default="No items found" />
  };
  static getDerivedStateFromProps(nextProps, prevState) {
    if (!nextProps.selection) {
      // next props doesn't have a selection, reset our state
      return {
        selection: []
      };
    }
    const controlledSelection = nextProps.selection.selected;
    const unfilteredSelection = controlledSelection ?? prevState.selection;

    // Ensure we're not including selections that aren't in the
    // current `items` array (affected by pagination)
    const {
      itemId,
      items
    } = nextProps;
    const selection = unfilteredSelection.filter(selectedItem => items.findIndex(item => getItemId(item, itemId) === getItemId(selectedItem, itemId)) !== -1);

    // If some selected items were filtered out, update state and callback
    if (selection.length !== unfilteredSelection.length) {
      nextProps.selection.onSelectionChange?.(selection);
      return {
        selection
      };
    }

    // Always update selection state from props if controlled
    if (controlledSelection) {
      return {
        selection
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.state = {
      // used for checking if  initial selection is rendered
      initialSelectionRendered: false,
      selection: []
    };
  }
  componentDidMount() {
    this.getInitialSelection();
  }
  componentDidUpdate() {
    this.getInitialSelection();
  }
  get pageSize() {
    return this.props.pagination?.pageSize ?? this.context.EuiTablePagination?.itemsPerPage ?? euiTablePaginationDefaults.itemsPerPage;
  }
  get isSelectionControlled() {
    return !!this.props.selection?.selected;
  }
  getInitialSelection() {
    if (this.isSelectionControlled) return;
    if (this.props.selection && this.props.selection.initialSelected && !this.state.initialSelectionRendered && this.props.items.length > 0) {
      this.setState({
        selection: this.props.selection.initialSelected,
        initialSelectionRendered: true
      });
    }
  }

  /**
   * @deprecated Use `selection.selected` instead to declaratively control table selection
   */
  setSelection(newSelection) {
    this.changeSelection(newSelection);
  }
  buildCriteria(props) {
    const criteria = {};
    if (hasPagination(props)) {
      criteria.page = {
        index: props.pagination.pageIndex,
        size: this.pageSize
      };
    }
    if (props.sorting) {
      criteria.sort = props.sorting.sort;
    }
    return criteria;
  }
  changeSelection(changedSelection) {
    const {
      selection
    } = this.props;
    if (!selection) return;
    selection.onSelectionChange?.(changedSelection);
    if (!this.isSelectionControlled) {
      this.setState({
        selection: changedSelection
      });
    }
  }
  clearSelection() {
    this.changeSelection([]);
  }
  onPageSizeChange(size) {
    this.clearSelection();
    const currentCriteria = this.buildCriteria(this.props);
    const criteria = {
      ...currentCriteria,
      page: {
        index: 0,
        // when page size changes, we take the user back to the first page
        size
      }
    };
    if (this.props.onChange) {
      this.props.onChange(criteria);
    }
  }
  onPageChange(index) {
    this.clearSelection();
    const currentCriteria = this.buildCriteria(this.props);
    const criteria = {
      ...currentCriteria,
      page: {
        ...currentCriteria.page,
        index
      }
    };
    if (this.props.onChange) {
      this.props.onChange(criteria);
    }
  }
  onColumnSortChange(column) {
    this.clearSelection();
    const currentCriteria = this.buildCriteria(this.props);
    let direction = SortDirection.ASC;
    if (currentCriteria && currentCriteria.sort && (currentCriteria.sort.field === column.field || currentCriteria.sort.field === column.name)) {
      direction = SortDirection.reverse(currentCriteria.sort.direction);
    }
    const criteria = {
      ...currentCriteria,
      // resetting the page if the criteria has one
      page: !currentCriteria.page ? undefined : {
        index: 0,
        size: currentCriteria.page.size
      },
      sort: {
        field: column.field || column.name,
        direction
      }
    };
    if (this.props.onChange) {
      // @ts-ignore complex relationship between pagination's existence and criteria, the code logic ensures this is correctly maintained
      this.props.onChange(criteria);
    }
  }
  tableId = htmlIdGenerator('__table')();
  render() {
    const {
      className,
      loading,
      items,
      itemId,
      columns,
      pagination,
      sorting,
      selection,
      onChange,
      error,
      noItemsMessage,
      compressed,
      itemIdToExpandedRowMap,
      responsive,
      isSelectable,
      isExpandable,
      hasActions,
      rowProps,
      cellProps,
      tableCaption,
      rowHeader,
      tableLayout,
      ...rest
    } = this.props;
    const classes = classNames('euiBasicTable', {
      'euiBasicTable-loading': loading
    }, className);
    const table = this.renderTable();
    const paginationBar = this.renderPaginationBar();
    return <div className={classes} {...rest}>
        {table}
        {paginationBar}
      </div>;
  }
  renderTable() {
    const {
      compressed,
      responsive,
      tableLayout,
      loading
    } = this.props;
    const mobileHeader = responsive ? <EuiTableHeaderMobile>
        <EuiFlexGroup responsive={false} justifyContent="spaceBetween" alignItems="baseline">
          <EuiFlexItem grow={false}>{this.renderSelectAll(true)}</EuiFlexItem>
          <EuiFlexItem grow={false}>{this.renderTableMobileSort()}</EuiFlexItem>
        </EuiFlexGroup>
      </EuiTableHeaderMobile> : undefined;
    const caption = this.renderTableCaption();
    const head = this.renderTableHead();
    const body = this.renderTableBody();
    const footer = this.renderTableFooter();
    return <div>
        {mobileHeader}
        <EuiTable id={this.tableId} tableLayout={tableLayout} responsive={responsive} compressed={compressed} css={loading && safariLoadingWorkaround}>
          {caption}
          {head}
          {body}
          {footer}
        </EuiTable>
      </div>;
  }
  renderTableMobileSort() {
    const {
      columns,
      sorting
    } = this.props;
    const items = [];
    if (!sorting) {
      return null;
    }
    columns.forEach((column, index) => {
      if (column.field && sorting.sort && !!sorting.enableAllColumns && column.sortable == null) {
        column = {
          ...column,
          sortable: true
        };
      }
      if (!column.sortable || column?.mobileOptions?.show === false) {
        return;
      }
      const sortDirection = this.resolveColumnSortDirection(column);
      items.push({
        name: column.name,
        key: `_data_s_${column.field}_${index}`,
        onSort: this.resolveColumnOnSort(column),
        isSorted: !!sortDirection,
        isSortAscending: sortDirection ? SortDirection.isAsc(sortDirection) : undefined
      });
    });
    return items.length ? <EuiTableSortMobile items={items} /> : null;
  }
  renderTableCaption() {
    const {
      items,
      pagination,
      tableCaption
    } = this.props;
    const itemCount = items.length;
    const totalItemCount = pagination ? pagination.totalItemCount : itemCount;
    const page = pagination ? pagination.pageIndex + 1 : 1;
    const pageCount = pagination ? Math.ceil(pagination.totalItemCount / this.pageSize) : 1;
    let captionElement;
    if (tableCaption) {
      if (pagination) {
        captionElement = <EuiI18n token="euiBasicTable.tableCaptionWithPagination" default="{tableCaption}; Page {page} of {pageCount}." values={{
          tableCaption,
          page,
          pageCount
        }} />;
      } else {
        captionElement = tableCaption;
      }
    } else {
      if (pagination) {
        if (pagination.totalItemCount > 0) {
          captionElement = <EuiI18n token="euiBasicTable.tableAutoCaptionWithPagination" default="This table contains {itemCount} rows out of {totalItemCount} rows; Page {page} of {pageCount}." values={{
            totalItemCount,
            itemCount,
            page,
            pageCount
          }} />;
        } else {
          captionElement = <EuiI18n token="euiBasicTable.tableSimpleAutoCaptionWithPagination" default="This table contains {itemCount} rows; Page {page} of {pageCount}." values={{
            itemCount,
            page,
            pageCount
          }} />;
        }
      } else {
        captionElement = <EuiI18n token="euiBasicTable.tableAutoCaptionWithoutPagination" default="This table contains {itemCount} rows." values={{
          itemCount
        }} />;
      }
    }
    return <EuiScreenReaderOnly>
        <caption className="euiTableCaption">
          <EuiDelayRender>{captionElement}</EuiDelayRender>
        </caption>
      </EuiScreenReaderOnly>;
  }
  selectAllIdGenerator = htmlIdGenerator('_selection_column-checkbox');
  renderSelectAll = isMobile => {
    const {
      items,
      selection
    } = this.props;
    if (!selection) {
      return;
    }
    const selectableItems = items.filter(item => !selection.selectable || selection.selectable(item));
    const checked = this.state.selection && selectableItems.length > 0 && this.state.selection.length === selectableItems.length;
    const disabled = selectableItems.length === 0;
    const onChange = event => {
      if (event.target.checked) {
        this.changeSelection(selectableItems);
      } else {
        this.changeSelection([]);
      }
    };
    return <EuiI18n token="euiBasicTable.selectAllRows" default="Select all rows">
        {selectAllRows => <EuiCheckbox id={this.selectAllIdGenerator(isMobile ? 'mobile' : 'desktop')} type={isMobile ? undefined : 'inList'} checked={checked} disabled={disabled} onChange={onChange}
      // Only add data-test-subj to one of the checkboxes
      data-test-subj={isMobile ? undefined : 'checkboxSelectAll'} aria-label={selectAllRows} label={isMobile ? selectAllRows : null} />}
      </EuiI18n>;
  };
  renderTableHead() {
    const {
      columns,
      selection
    } = this.props;
    const headers = [];
    if (selection) {
      headers.push(<EuiTableHeaderCellCheckbox key="_selection_column_h">
          {this.renderSelectAll(false)}
        </EuiTableHeaderCellCheckbox>);
    }
    columns.forEach((column, index) => {
      const {
        field,
        width,
        name,
        align,
        dataType,
        sortable,
        mobileOptions,
        readOnly,
        description
      } = column;
      const columnAlign = align || this.getAlignForDataType(dataType);

      // actions column
      if (column.actions) {
        headers.push(<EuiTableHeaderCell key={`_actions_h_${index}`} align="right" width={width} description={description} mobileOptions={mobileOptions}>
            {name}
          </EuiTableHeaderCell>);
        return;
      }

      // computed column
      if (!column.field) {
        const sorting = {};
        // computed columns are only sortable if their `sortable` is a function
        if (this.props.sorting && typeof sortable === 'function') {
          const sortDirection = this.resolveColumnSortDirection(column);
          sorting.isSorted = !!sortDirection;
          sorting.isSortAscending = sortDirection ? SortDirection.isAsc(sortDirection) : undefined;
          sorting.onSort = this.resolveColumnOnSort(column);
          sorting.readOnly = this.props.sorting.readOnly || readOnly;
        }
        headers.push(<EuiTableHeaderCell key={`_computed_column_h_${index}`} align={columnAlign} width={width} mobileOptions={mobileOptions} data-test-subj={`tableHeaderCell_${typeof name === 'string' ? name : ''}_${index}`} description={description} {...sorting}>
            {name}
          </EuiTableHeaderCell>);
        return;
      }

      // field data column
      const sorting = {};
      if (this.props.sorting) {
        if (this.props.sorting.sort && !!this.props.sorting.enableAllColumns && column.sortable == null) {
          column = {
            ...column,
            sortable: true
          };
        }
        const {
          sortable
        } = column;
        if (sortable) {
          const sortDirection = this.resolveColumnSortDirection(column);
          sorting.isSorted = !!sortDirection;
          sorting.isSortAscending = sortDirection ? SortDirection.isAsc(sortDirection) : undefined;
          sorting.onSort = this.resolveColumnOnSort(column);
          sorting.readOnly = this.props.sorting.readOnly || readOnly;
        }
      }
      headers.push(<EuiTableHeaderCell key={`_data_h_${field}_${index}`} align={columnAlign} width={width} mobileOptions={mobileOptions} data-test-subj={`tableHeaderCell_${field}_${index}`} description={description} {...sorting}>
          {name}
        </EuiTableHeaderCell>);
    });
    return <EuiTableHeader>{headers}</EuiTableHeader>;
  }
  renderTableFooter() {
    const {
      items,
      columns,
      pagination,
      selection
    } = this.props;
    const footers = [];
    let hasDefinedFooter = false;
    if (selection) {
      // Create an empty cell to compensate for additional selection column
      footers.push(<EuiTableFooterCell key="_selection_column_f">
          {undefined}
        </EuiTableFooterCell>);
    }
    columns.forEach(column => {
      const footer = getColumnFooter(column, {
        items,
        pagination
      });
      const {
        mobileOptions,
        field,
        align
      } = column;
      if (mobileOptions?.only) {
        return; // exclude columns that only exist for mobile headers
      }

      if (footer) {
        footers.push(<EuiTableFooterCell key={`footer_${field}_${footers.length - 1}`} align={align}>
            {footer}
          </EuiTableFooterCell>);
        hasDefinedFooter = true;
      } else {
        // Footer is undefined, so create an empty cell to preserve layout
        footers.push(<EuiTableFooterCell key={`footer_empty_${footers.length - 1}`} align={align}>
            {undefined}
          </EuiTableFooterCell>);
      }
    });
    return footers.length && hasDefinedFooter ? <EuiTableFooter>{footers}</EuiTableFooter> : null;
  }
  renderTableBody() {
    const {
      error,
      loading,
      items
    } = this.props;
    let content;
    if (error) {
      content = this.renderErrorMessage(error);
    } else if (items.length === 0) {
      content = this.renderEmptyMessage();
    } else {
      content = items.map((item, index) => {
        // if there's pagination the item's index must be adjusted to the where it is in the whole dataset
        const tableItemIndex = hasPagination(this.props) && this.pageSize > 0 ? this.props.pagination.pageIndex * this.pageSize + index : index;
        return this.renderItemRow(item, tableItemIndex);
      });
    }
    return <RenderWithEuiTheme>
        {theme => <EuiTableBody css={loading && euiBasicTableBodyLoading(theme)}>
            {content}
          </EuiTableBody>}
      </RenderWithEuiTheme>;
  }
  renderErrorMessage(error) {
    const colSpan = this.props.columns.length + (this.props.selection ? 1 : 0);
    return <EuiTableRow>
        <EuiTableRowCell align="center" colSpan={colSpan} mobileOptions={{
        width: '100%'
      }}>
          <EuiIcon type="minusInCircle" color="danger" /> {error}
        </EuiTableRowCell>
      </EuiTableRow>;
  }
  renderEmptyMessage() {
    const {
      columns,
      selection,
      noItemsMessage
    } = this.props;
    const colSpan = columns.length + (selection ? 1 : 0);
    return <EuiTableRow>
        <EuiTableRowCell align="center" colSpan={colSpan} mobileOptions={{
        width: '100%'
      }}>
          {noItemsMessage}
        </EuiTableRowCell>
      </EuiTableRow>;
  }
  renderItemRow(item, rowIndex) {
    const {
      columns,
      selection,
      isSelectable,
      hasActions,
      rowHeader,
      itemIdToExpandedRowMap = {},
      isExpandable
    } = this.props;
    const cells = [];
    const {
      itemId: itemIdCallback
    } = this.props;
    const itemId = getItemId(item, itemIdCallback) != null ? getItemId(item, itemIdCallback) : rowIndex;
    const selected = !selection ? false : this.state.selection && !!this.state.selection.find(selectedItem => getItemId(selectedItem, itemIdCallback) === itemId);
    let calculatedHasSelection;
    if (selection) {
      cells.push(this.renderItemSelectionCell(itemId, item, selected));
      calculatedHasSelection = true;
    }
    let calculatedHasActions;
    columns.forEach((column, columnIndex) => {
      if (column.actions) {
        cells.push(this.renderItemActionsCell(itemId, item, column, columnIndex));
        calculatedHasActions = true;
      } else if (column.field) {
        const fieldDataColumn = column;
        cells.push(this.renderItemFieldDataCell(itemId, item, column, columnIndex, fieldDataColumn.field === rowHeader));
      } else {
        cells.push(this.renderItemComputedCell(itemId, item, column, columnIndex));
      }
    });

    // Occupy full width of table, taking checkbox & mobile only columns into account.
    let expandedRowColSpan = selection ? columns.length + 1 : columns.length;
    const mobileOnlyCols = columns.reduce((num, column) => {
      return column?.mobileOptions?.only ? num + 1 : num + 0; // BWC only
    }, 0);
    expandedRowColSpan = expandedRowColSpan - mobileOnlyCols;

    // We'll use the ID to associate the expanded row with the original.
    const hasExpandedRow = itemIdToExpandedRowMap.hasOwnProperty(itemId);
    const expandedRowId = hasExpandedRow ? `row_${itemId}_expansion` : undefined;
    const expandedRow = hasExpandedRow ? <EuiTableRow id={expandedRowId} isExpandedRow={true} isSelectable={isSelectable}>
        <EuiTableRowCell colSpan={expandedRowColSpan} textOnly={false}>
          {itemIdToExpandedRowMap[itemId]}
        </EuiTableRowCell>
      </EuiTableRow> : undefined;
    const {
      rowProps: rowPropsCallback
    } = this.props;
    const rowProps = getRowProps(item, rowPropsCallback);
    const row = <EuiTableRow aria-owns={expandedRowId} isSelectable={isSelectable == null ? calculatedHasSelection : isSelectable} isSelected={selected} hasActions={hasActions == null ? calculatedHasActions : hasActions} isExpandable={isExpandable} {...rowProps}>
        {cells}
      </EuiTableRow>;
    return <Fragment key={`row_${itemId}`}>
        {row}
        {expandedRow}
      </Fragment>;
  }
  renderItemSelectionCell(itemId, item, selected) {
    const {
      selection
    } = this.props;
    const key = `_selection_column_${itemId}`;
    const checked = selected;
    const disabled = selection.selectable && !selection.selectable(item);
    const title = selection.selectableMessage && selection.selectableMessage(!disabled, item);
    const onChange = event => {
      if (event.target.checked) {
        this.changeSelection([...this.state.selection, item]);
      } else {
        const {
          itemId: itemIdCallback
        } = this.props;
        this.changeSelection(this.state.selection.reduce((selection, selectedItem) => {
          if (getItemId(selectedItem, itemIdCallback) !== itemId) {
            selection.push(selectedItem);
          }
          return selection;
        }, []));
      }
    };
    return <EuiTableRowCellCheckbox key={key}>
        <EuiI18n token="euiBasicTable.selectThisRow" default="Select this row">
          {selectThisRow => <EuiCheckbox id={`${this.tableId}${key}-checkbox`} type="inList" disabled={disabled} checked={checked} onChange={onChange} title={title || selectThisRow} aria-label={title || selectThisRow} data-test-subj={`checkboxSelectRow-${itemId}`} />}
        </EuiI18n>
      </EuiTableRowCellCheckbox>;
  }
  renderItemActionsCell(itemId, item, column, columnIndex) {
    const actionEnabled = action => this.state.selection.length === 0 && (!action.enabled || action.enabled(item));
    let actualActions = column.actions.filter(action => !action.available || action.available(item));
    if (actualActions.length > 2) {
      // if any of the actions `isPrimary`, add them inline as well, but only the first 2
      const primaryActions = actualActions.filter(o => o.isPrimary);
      actualActions = primaryActions.slice(0, 2);

      // if we have more than 1 action, we don't show them all in the cell, instead we
      // put them all in a popover tool. This effectively means we can only have a maximum
      // of one tool per row (it's either and normal action, or it's a popover that shows multiple actions)
      //
      // here we create a single custom action that triggers the popover with all the configured actions

      actualActions.push({
        name: 'All actions',
        render: item => {
          return <CollapsedItemActions actions={column.actions} itemId={itemId} item={item} actionEnabled={actionEnabled} />;
        }
      });
    }
    const tools = <ExpandedItemActions actions={actualActions} itemId={itemId} item={item} actionEnabled={actionEnabled} />;
    const key = `record_actions_${itemId}_${columnIndex}`;
    return <EuiTableRowCell showOnHover={true} key={key} align="right" textOnly={false} hasActions={true} css={euiBasicTableActionsWrapper}>
        {tools}
      </EuiTableRowCell>;
  }
  renderItemFieldDataCell(itemId, item, column, columnIndex, setScopeRow) {
    const {
      field,
      render,
      dataType
    } = column;
    const key = `_data_column_${field}_${itemId}_${columnIndex}`;
    const contentRenderer = render || this.getRendererForDataType(dataType);
    const value = get(item, field);
    const content = contentRenderer(value, item);
    return this.renderItemCell(item, column, key, content, setScopeRow);
  }
  renderItemComputedCell(itemId, item, column, columnIndex) {
    const {
      render
    } = column;
    const key = `_computed_column_${itemId}_${columnIndex}`;
    const contentRenderer = render || this.getRendererForDataType();
    const content = contentRenderer(item);
    return this.renderItemCell(item, column, key, content, false);
  }
  renderItemCell(item, column, key, content, setScopeRow) {
    const {
      align,
      render,
      dataType,
      isExpander,
      textOnly,
      name,
      field,
      description,
      sortable,
      footer,
      mobileOptions,
      ...rest
    } = column;
    const columnAlign = align || this.getAlignForDataType(dataType);
    const {
      cellProps: cellPropsCallback
    } = this.props;
    const cellProps = getCellProps(item, column, cellPropsCallback);
    return <EuiTableRowCell key={key} align={columnAlign} isExpander={isExpander} textOnly={textOnly || !render} setScopeRow={setScopeRow} mobileOptions={{
      ...mobileOptions,
      render: mobileOptions && mobileOptions.render && mobileOptions.render(item),
      header: mobileOptions && mobileOptions.header === false ? false : name
    }} {...cellProps} {...rest}>
        {content}
      </EuiTableRowCell>;
  }
  resolveColumnSortDirection = column => {
    const {
      sorting
    } = this.props;
    const {
      sortable,
      field,
      name
    } = column;
    if (!sorting || !sorting.sort || !sortable) {
      return;
    }
    if (sorting.sort.field === field || sorting.sort.field === name) {
      return sorting.sort.direction;
    }
  };
  resolveColumnOnSort = column => {
    const {
      sorting
    } = this.props;
    const {
      sortable,
      name
    } = column;
    if (!sorting || !sortable) {
      return;
    }
    if (!this.props.onChange) {
      throw new Error(`BasicTable is configured to be sortable on column [${name}] but
          [onChange] is not configured. This callback must be implemented to handle the sort requests`);
    }
    return () => this.onColumnSortChange(column);
  };
  getRendererForDataType(dataType = 'auto') {
    const profile = dataTypesProfiles[dataType];
    if (!profile) {
      throw new Error(`Unknown dataType [${dataType}]. The supported data types are [${DATA_TYPES.join(', ')}]`);
    }
    return profile.render;
  }
  getAlignForDataType(dataType = 'auto') {
    const profile = dataTypesProfiles[dataType];
    if (!profile) {
      throw new Error(`Unknown dataType [${dataType}]. The supported data types are [${DATA_TYPES.join(', ')}]`);
    }
    return profile.align;
  }
  renderPaginationBar() {
    const {
      error,
      pagination,
      tableCaption,
      onChange
    } = this.props;
    if (!error && pagination && pagination.totalItemCount > 0) {
      if (!onChange) {
        throw new Error(`The Basic Table is configured with pagination but [onChange] is
        not configured. This callback must be implemented to handle pagination changes`);
      }
      return <EuiI18n token="euiBasicTable.tablePagination" default="Pagination for table: {tableCaption}" values={{
        tableCaption
      }}>
          {tablePagination => <PaginationBar pagination={pagination} onPageSizeChange={this.onPageSizeChange.bind(this)} onPageChange={this.onPageChange.bind(this)} aria-controls={this.tableId} aria-label={tablePagination} />}
        </EuiI18n>;
    }
  }
}
EuiBasicTable.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Describes how to extract a unique ID from each item, used for selections & expanded rows
     */
  /**
     * Describes how to extract a unique ID from each item, used for selections & expanded rows
     */
  itemId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired, PropTypes.func.isRequired]),
  /**
     * Row expansion uses the itemId prop to identify each row
     */
  /**
     * Row expansion uses the itemId prop to identify each row
     */
  itemIdToExpandedRowMap: PropTypes.shape({}),
  /**
     * A list of objects to appear in the table - an item per row
     */
  /**
     * A list of objects to appear in the table - an item per row
     */
  items: PropTypes.arrayOf(PropTypes.any.isRequired),
  /**
     * Applied to `EuiTableRowCell`
     */
  /**
     * Applied to `EuiTableRowCell`
     */
  cellProps: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.func.isRequired]),
  /**
     * An array of one of the objects: #EuiTableFieldDataColumnType, #EuiTableComputedColumnType or #EuiTableActionsColumnType.
     */
  /**
     * An array of one of the objects: #EuiTableFieldDataColumnType, #EuiTableComputedColumnType or #EuiTableActionsColumnType.
     */
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    /**
       * A field of the item (may be a nested field)
       */
    // type hack used for better autocomplete support
    // https://github.com/microsoft/TypeScript/issues/29729
    field: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired,
    // supports outer.inner key paths
    /**
       * The display name of the column
       */
    name: PropTypes.node.isRequired,
    /**
       * A description of the column (will be presented as a title over the column header)
       */
    description: PropTypes.string,
    /**
       * Describes the data types of the displayed value (serves as a rendering hint for the table)
       */
    dataType: PropTypes.oneOf(["auto", "string", "number", "boolean", "date"]),
    /**
       * A CSS width property. Hints for the required width of the column (e.g. "30%", "100px", etc..)
       */
    width: PropTypes.string,
    /**
       * Defines whether the user can sort on this column. If a function is provided, this function returns the value to sort against
       */
    sortable: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.func.isRequired]),
    isExpander: PropTypes.bool,
    /**
       * Creates a text wrapper around cell content that helps word break or truncate
       * long text correctly.
       */
    textOnly: PropTypes.bool,
    /**
       * Defines the horizontal alignment of the column
       */
    align: PropTypes.any,
    /**
       * Indicates whether this column should truncate overflowing text content.
       * - Set to `true` to enable single-line truncation.
       * - To enable multi-line truncation, use a configuration object with `lines`
       * set to a number of lines to truncate to.
       */
    truncateText: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
      lines: PropTypes.number.isRequired
    }).isRequired]),
    mobileOptions: PropTypes.shape({
      render: PropTypes.func
    }),
    /**
       * Describe a custom renderer function for the content
       */
    render: PropTypes.func,
    /**
       * Content to display in the footer beneath this column
       */
    footer: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired, PropTypes.func.isRequired]),
    /**
       * Disables the user's ability to change the sort but still shows the current direction
       */
    readOnly: PropTypes.bool,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired, PropTypes.shape({
    /**
       * A function that computes the value for each item and renders it
       */
    render: PropTypes.func.isRequired,
    /**
       * The display name of the column
       */
    name: PropTypes.node,
    /**
       * A description of the column (will be presented as a title over the column header
       */
    description: PropTypes.string,
    /**
       * If provided, allows this column to be sorted on. Must return the value to sort against.
       */
    sortable: PropTypes.func,
    /**
       * A CSS width property. Hints for the required width of the column
       */
    width: PropTypes.string,
    /**
       * Indicates whether this column should truncate its content when it doesn't fit
       */
    truncateText: PropTypes.bool,
    isExpander: PropTypes.bool,
    align: PropTypes.any,
    /**
       * Disables the user's ability to change the sort but still shows the current direction
       */
    readOnly: PropTypes.bool,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired, PropTypes.shape({
    /**
       * An array of one of the objects: #DefaultItemAction or #CustomItemAction
       */
    actions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
      /**
         * The type of action
         */
      type: PropTypes.oneOfType([PropTypes.oneOf(["button"]), PropTypes.oneOf(["icon"]).isRequired]),
      /**
         * Defines the color of the button
         */
      color: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.func.isRequired]),
      /**
         * The display name of the action (will render as visible text if rendered within a collapsed menu)
         */
      /**
         * The display name of the action (will render as visible text if rendered within a collapsed menu)
         */
      name: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.func.isRequired]).isRequired,
      /**
         * Describes the action (will render as tooltip content)
         */
      /**
         * Describes the action (will render as tooltip content)
         */
      description: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired]).isRequired,
      /**
         * A handler function to execute the action
         */
      /**
         * A handler function to execute the action
         */
      onClick: PropTypes.func,
      href: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired]),
      target: PropTypes.string,
      /**
         * A callback function that determines whether the action is available
         */
      /**
         * A callback function that determines whether the action is available
         */
      available: PropTypes.func,
      /**
         * A callback function that determines whether the action is enabled
         */
      /**
         * A callback function that determines whether the action is enabled
         */
      enabled: PropTypes.func,
      isPrimary: PropTypes.bool,
      "data-test-subj": PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired]),
      /**
         * Associates an icon with the button
         */
      icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.func.isRequired])
    }).isRequired, PropTypes.shape({
      /**
         * Allows rendering a totally custom action
         */
      render: PropTypes.func.isRequired,
      /**
         * A callback that defines whether the action is available
         */
      available: PropTypes.func,
      /**
         * A callback that defines whether the action is enabled
         */
      enabled: PropTypes.func,
      isPrimary: PropTypes.bool
    }).isRequired]).isRequired).isRequired,
    /**
       * The display name of the column
       */
    name: PropTypes.node,
    /**
       * A description of the column (will be presented as a title over the column header
       */
    description: PropTypes.string,
    /**
       * A CSS width property. Hints for the required width of the column
       */
    width: PropTypes.string
  }).isRequired]).isRequired),
  /**
     * Error message to display
     */
  /**
     * Error message to display
     */
  error: PropTypes.string,
  /**
     * Describes the content of the table. If not specified, the caption will be "This table contains {itemCount} rows."
     */
  /**
     * Describes the content of the table. If not specified, the caption will be "This table contains {itemCount} rows."
     */
  tableCaption: PropTypes.string,
  /**
     * Indicates which column should be used as the identifying cell in each row. Should match a "field" prop in FieldDataColumn
     */
  /**
     * Indicates which column should be used as the identifying cell in each row. Should match a "field" prop in FieldDataColumn
     */
  rowHeader: PropTypes.string,
  hasActions: PropTypes.bool,
  isExpandable: PropTypes.bool,
  isSelectable: PropTypes.bool,
  /**
     * Provides an infinite loading indicator
     */
  /**
     * Provides an infinite loading indicator
     */
  loading: PropTypes.bool,
  /**
     * Message to display if table is empty
     */
  /**
     * Message to display if table is empty
     */
  noItemsMessage: PropTypes.node,
  /**
     * Called whenever pagination or sorting changes (this property is required when either pagination or sorting is configured). See #Criteria or #CriteriaWithPagination
     */
  onChange: PropTypes.func,
  /**
     * Configures #Pagination
     */
  pagination: PropTypes.oneOfType([PropTypes.oneOf([undefined]), PropTypes.shape({
    /**
       * The current page (zero-based) index
       */
    pageIndex: PropTypes.number.isRequired,
    /**
       * The maximum number of items that can be shown in a single page.
       * Pass `0` to display the selected "Show all" option and hide the pagination.
       *
       * @default 10
       */
    pageSize: PropTypes.number,
    /**
       * The total number of items the page is "sliced" of
       */
    totalItemCount: PropTypes.number.isRequired,
    /**
       * Configures the page size dropdown options.
       * Pass `0` as one of the options to create a "Show all" option.
       *
       * @default [10, 25, 50]
       */
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number.isRequired),
    /**
       * Set to false to hide the page size dropdown
       *
       * @default true
       */
    showPerPageOptions: PropTypes.bool
  })]),
  /**
     * If true, will convert table to cards in mobile view
     */
  /**
     * If true, will convert table to cards in mobile view
     */
  responsive: PropTypes.bool,
  /**
     * Applied to `EuiTableRow`
     */
  /**
     * Applied to `EuiTableRow`
     */
  rowProps: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.func.isRequired]),
  /**
     * Configures #EuiTableSelectionType
     */
  /**
     * Configures #EuiTableSelectionType
     */
  selection: PropTypes.shape({
    /**
       * A callback that will be called whenever the item selection changes.
       *
       * Required if `selected` is passed.
       */
    onSelectionChange: PropTypes.func,
    /**
       * A callback that is called per item to indicate whether it is selectable
       */
    selectable: PropTypes.func,
    /**
       * A callback that is called per item to retrieve a message for its selectable state.
       * We display these messages as a tooltip on an unselectable checkbox
       */
    selectableMessage: PropTypes.func,
    /**
       * Sets initially selected items. Use for uncontrolled selection behavior (checkbox
       * will only change from user input, and not from developer control).
       *
       * This prop will be ignored if `selected` is passed.
       */
    initialSelected: PropTypes.arrayOf(PropTypes.any.isRequired),
    /**
       * Used for controlled selection behavior, e.g. when you want to programmatically
       * control which selection checkboxes are checked, and which are not.
       */
    selected: PropTypes.arrayOf(PropTypes.any.isRequired)
  }),
  /**
     * Configures #EuiTableSortingType
     */
  /**
     * Configures #EuiTableSortingType
     */
  sorting: PropTypes.shape({
    /**
       * Indicates the property/field to sort on
       */
    sort: PropTypes.shape({
      field: PropTypes.any.isRequired,
      direction: PropTypes.oneOf(["asc", "desc"]).isRequired
    }),
    /**
       * Enables/disables unsorting of table columns. Supported by EuiInMemoryTable.
       */
    allowNeutralSort: PropTypes.bool,
    /**
       * Enables the default sorting ability for each column.
       */
    enableAllColumns: PropTypes.bool,
    /**
       * Disables the user's ability to change the sort but still shows the current direction
       */
    readOnly: PropTypes.bool
  }),
  /**
     * Sets the table-layout CSS property. Note that auto tableLayout prevents truncateText from working properly.
     */
  /**
     * Sets the table-layout CSS property. Note that auto tableLayout prevents truncateText from working properly.
     */
  tableLayout: PropTypes.oneOf(["fixed", "auto"]),
  /**
     * Applied to table cells. Any cell using a render function will set this to be false.
     *
     * Creates a text wrapper around cell content that helps word break or truncate
     * long text correctly.
     */
  /**
     * Applied to table cells. Any cell using a render function will set this to be false.
     *
     * Creates a text wrapper around cell content that helps word break or truncate
     * long text correctly.
     */
  textOnly: PropTypes.bool
};
try {
  EuiBasicTable.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/basic_table/basic_table.tsx',
    description: '',
    displayName: 'EuiBasicTable',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      itemId: {
        defaultValue: null,
        description: 'Describes how to extract a unique ID from each item, used for selections & expanded rows',
        name: 'itemId',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'ItemId<T>'
        }
      },
      itemIdToExpandedRowMap: {
        defaultValue: null,
        description: 'Row expansion uses the itemId prop to identify each row',
        name: 'itemIdToExpandedRowMap',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'ItemIdToExpandedRowMap'
        }
      },
      items: {
        defaultValue: null,
        description: 'A list of objects to appear in the table - an item per row',
        name: 'items',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: true,
        type: {
          name: 'T[]'
        }
      },
      cellProps: {
        defaultValue: null,
        description: 'Applied to `EuiTableRowCell`',
        name: 'cellProps',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'object | CellPropsCallback<T>'
        }
      },
      columns: {
        defaultValue: null,
        description: 'An array of one of the objects: #EuiTableFieldDataColumnType, #EuiTableComputedColumnType or #EuiTableActionsColumnType.',
        name: 'columns',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: true,
        type: {
          name: 'EuiBasicTableColumn<T>[]'
        }
      },
      error: {
        defaultValue: null,
        description: 'Error message to display',
        name: 'error',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      tableCaption: {
        defaultValue: null,
        description: 'Describes the content of the table. If not specified, the caption will be "This table contains {itemCount} rows."',
        name: 'tableCaption',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rowHeader: {
        defaultValue: null,
        description: 'Indicates which column should be used as the identifying cell in each row. Should match a "field" prop in FieldDataColumn',
        name: 'rowHeader',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      hasActions: {
        defaultValue: null,
        description: '',
        name: 'hasActions',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isExpandable: {
        defaultValue: null,
        description: '',
        name: 'isExpandable',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSelectable: {
        defaultValue: null,
        description: '',
        name: 'isSelectable',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      loading: {
        defaultValue: null,
        description: 'Provides an infinite loading indicator',
        name: 'loading',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      noItemsMessage: {
        defaultValue: {
          value: '(\n' + '      <EuiI18n token="euiBasicTable.noItemsMessage" default="No items found" />\n' + '    )'
        },
        description: 'Message to display if table is empty',
        name: 'noItemsMessage',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'Called whenever pagination or sorting changes (this property is required when either pagination or sorting is configured). See #Criteria or #CriteriaWithPagination',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '((criteria: Criteria<T>) => void) | ((criteria: CriteriaWithPagination<T>) => void)'
        }
      },
      pagination: {
        defaultValue: null,
        description: 'Configures #Pagination',
        name: 'pagination',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Pagination'
        }
      },
      responsive: {
        defaultValue: {
          value: 'true'
        },
        description: 'If true, will convert table to cards in mobile view',
        name: 'responsive',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      rowProps: {
        defaultValue: null,
        description: 'Applied to `EuiTableRow`',
        name: 'rowProps',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'object | RowPropsCallback<T>'
        }
      },
      selection: {
        defaultValue: null,
        description: 'Configures #EuiTableSelectionType',
        name: 'selection',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'EuiTableSelectionType<T>'
        }
      },
      sorting: {
        defaultValue: null,
        description: 'Configures #EuiTableSortingType',
        name: 'sorting',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'EuiTableSortingType<T>'
        }
      },
      tableLayout: {
        defaultValue: {
          value: 'fixed'
        },
        description: 'Sets the table-layout CSS property. Note that auto tableLayout prevents truncateText from working properly.',
        name: 'tableLayout',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"fixed" | "auto"',
          value: [{
            value: '"fixed"'
          }, {
            value: '"auto"'
          }]
        }
      },
      textOnly: {
        defaultValue: null,
        description: 'Applied to table cells. Any cell using a render function will set this to be false.\n' + '\n' + 'Creates a text wrapper around cell content that helps word break or truncate\n' + 'long text correctly.',
        name: 'textOnly',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        }, {
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'BasicTableProps', 'TableHTMLAttributes', 'EuiTableProps']
  };
} catch (e) {}